import React, { useState, useEffect } from "react";
import { fetchUsers, saveUser, sendEmail } from "./service/APIService";
import { Link } from "react-router-dom";
import { FiX } from "react-icons/fi";
import "./styles/LoginPage.css";
import config from "./config.json";
import pocketappslogo from "./images/pocketappslogo.png";

const isValidEmail = (val) => {
  if (!val || typeof val !== "string") return false;
  const clean = val.trim().toLowerCase();
  return clean !== "guest" && clean.includes("@") && clean.includes(".") && !clean.includes(" ");
};

const getValidSavedEmail = () => {
  const remembered = localStorage.getItem("rememberedEmail");
  if (isValidEmail(remembered)) {
    return remembered.trim().toLowerCase();
  }
  if (remembered) {
    localStorage.removeItem("rememberedEmail");
  }
  const userEmail = localStorage.getItem("loggedInUserEmail");
  if (isValidEmail(userEmail)) {
    return userEmail.trim().toLowerCase();
  }
  if (userEmail && userEmail !== "guest" && !isValidEmail(userEmail)) {
    localStorage.removeItem("loggedInUserEmail");
  }
  return "";
};
const maskEmail = (emailStr) => {
  if (!emailStr || typeof emailStr !== "string" || !emailStr.includes("@")) return "";
  const [local, domain] = emailStr.split("@");
  const prefix = local.slice(0, 3);
  const domainParts = domain ? domain.split(".") : [];
  const ext = domainParts.length > 1 ? "." + domainParts.slice(1).join(".") : ".com";
  return `${prefix}*******@****${ext}`;
};

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [isEmailMasked, setIsEmailMasked] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Biometric State
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState(null);

  const getBiometricLabel = (type) => {
    if (type === "FaceID") return "Face ID";
    if (type === "TouchID") return "Touch ID";
    return "Fingerprint";
  };

  const savedEmail = getValidSavedEmail();
  const hasLoggedInOnce = localStorage.getItem("hasLoggedInOnce") === "true";
  const isBiometricExplicitlyDisabled = localStorage.getItem("biometricEnabled") === "false";
  const isBiometricExplicitlyEnabled = localStorage.getItem("biometricEnabled") === "true";

  // Biometric is only permitted and shown if:
  // 1. User has successfully logged in before (hasLoggedInOnce === true)
  // 2. User has a valid saved email from a prior successful login
  // 3. User has NOT disabled Face ID / Biometrics in Profile
  // 4. Biometric was enabled
  // 5. Hardware biometric is available (if running in React Native WebView)
  const canShowFaceId = Boolean(hasLoggedInOnce && isBiometricExplicitlyEnabled && !isBiometricExplicitlyDisabled && savedEmail && (!window.ReactNativeWebView || biometricAvailable));

  useEffect(() => {
    // Sanitize any legacy dirty values (such as "vbal")
    const curRemembered = localStorage.getItem("rememberedEmail");
    if (curRemembered && !isValidEmail(curRemembered)) {
      localStorage.removeItem("rememberedEmail");
    }
    const curLoggedIn = localStorage.getItem("loggedInUserEmail");
    if (curLoggedIn && curLoggedIn !== "guest" && !isValidEmail(curLoggedIn)) {
      localStorage.removeItem("loggedInUserEmail");
    }

    const validSaved = getValidSavedEmail();
    if (validSaved) {
      setEmail(validSaved);
      setIsEmailMasked(true);
    }
  }, []);

  // Listen to native Biometric (Face ID / Touch ID / Biometrics) messages
  useEffect(() => {
    const handleNativeMessage = async (event) => {
      try {
        let data = event.data;
        if (typeof data === "string") {
          data = JSON.parse(data);
        }
        if (!data || !data.type) return;

        if (data.type === "BIOMETRIC_CHECK_RESULT") {
          setBiometricAvailable(!!data.available);
          const type = data.biometryType || null;
          if (type) {
            setBiometryType(type);
          }
        } else if (data.type === "BIOMETRIC_VERIFY_RESULT") {
          if (data.success) {
            const targetEmail = getValidSavedEmail();
            const isBioDisabled = localStorage.getItem("biometricEnabled") === "false";
            const isBioEnabled = localStorage.getItem("biometricEnabled") === "true";
            const hasLoggedIn = localStorage.getItem("hasLoggedInOnce") === "true";
            const eligible = hasLoggedIn && isBioEnabled && !isBioDisabled && !!targetEmail;

            if (eligible && targetEmail) {
              setLoading(true);
              let storedUser = await fetchUsers(targetEmail);
              if (!storedUser) {
                storedUser = {
                  email: targetEmail,
                  name: targetEmail.split("@")[0],
                  country: "us",
                };
                await saveUser(storedUser);
              }
              localStorage.setItem("biometricEnabled", "true");
              localStorage.setItem("hasLoggedInOnce", "true");
              localStorage.setItem("rememberedEmail", storedUser.email);
              localStorage.setItem("loggedInUserEmail", storedUser.email);
              onLogin({
                email: storedUser.email,
                name: storedUser.name || storedUser.email.split("@")[0],
                country: storedUser.country || "",
              });
              setLoading(false);
            } else {
              setError("Please sign in with your email and verification code first.");
            }
          } else if (data.error && data.error !== "Cancelled." && !data.error.toLowerCase().includes("cancel")) {
            setError(data.error);
          }
        }
      } catch {
        // Ignore non-json
      }
    };

    window.addEventListener("message", handleNativeMessage);
    document.addEventListener("message", handleNativeMessage);

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "BIOMETRIC_CHECK" }));
    }

    return () => {
      window.removeEventListener("message", handleNativeMessage);
      document.removeEventListener("message", handleNativeMessage);
    };
  }, [onLogin]);

  const handleBiometricLogin = () => {
    setError("");
    const targetEmail = getValidSavedEmail();
    if (!targetEmail || !canShowFaceId) {
      setError(`Please sign in with your email first to enable ${getBiometricLabel(biometryType)}.`);
      return;
    }
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "BIOMETRIC_VERIFY",
          reason: `Sign in as ${targetEmail}`,
        }),
      );
    }
  };

  const generateOtp = () => Math.floor(100000 + Math.random() * 900000).toString();

  // Send OTP for Sign In
  const handleSendLoginOtp = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const cleanEmail = (email || "").trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    // App Review demo account: fixed code, no email sent, so reviewers can sign in
    // without receiving mail (App Store Guideline 2.1(a)).
    const demoEmail = (config.demoemail || "").trim().toLowerCase();
    if (demoEmail && cleanEmail === demoEmail && config.demootp) {
      setGeneratedOtp(String(config.demootp));
      setIsOtpSent(true);
      setOtp("");
      setError("");
      return;
    }

    setSendingOtp(true);
    setError("");

    try {
      const otpCode = generateOtp();
      setGeneratedOtp(otpCode);
      const requestDateTime = new Date().toLocaleString();
      const htmlContent = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 460px; margin: 0 auto; padding: 24px; border: 1px solid #EDE8EF; border-radius: 16px; background: #FFFFFF;">
          <h2 style="color: #42224A; margin: 0 0 8px 0; font-size: 20px;">${config.apptitle}</h2>
          <p style="color: #555; font-size: 14px; margin-bottom: 20px;">Use the verification code below to sign in to your account:</p>
          <div style="background: #F7F4F7; padding: 18px; border-radius: 12px; text-align: center; margin-bottom: 20px;">
            <span style="font-size: 32px; font-weight: 700; letter-spacing: 6px; color: #EF8767;">${otpCode}</span>
          </div>
          <p style="color: #888; font-size: 12px; margin: 0;">Requested on: ${requestDateTime}. Single-use code.</p>
        </div>
      `;

      const emailData = {
        sender: process.env.REACT_APP_SENDEREMAIL || "Pocket Apps <support@theuniquecreations.com>",
        recipient: cleanEmail,
        subject: `${config.apptitle} Sign In Verification Code`,
        cc: cleanEmail,
        body: htmlContent,
      };

      const sendRes = await sendEmail(emailData);
      if (sendRes && sendRes.error && !sendRes.success) {
        throw new Error(sendRes.error);
      }
      setIsOtpSent(true);
      setError("");
      setOtp("");
    } catch (err) {
      console.error("Failed to send sign-in OTP", err);
      setError("Failed to send OTP. Please check your email and connection.");
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP and Log In
  const handleVerifyLogin = async (e, directOtp) => {
    if (e && e.preventDefault) e.preventDefault();
    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanOtp = (directOtp !== undefined ? directOtp : otp || "").trim();

    if (!cleanOtp) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    if (cleanOtp !== generatedOtp.trim()) {
      setError("Invalid verification code. Please check your email or tap Resend.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let storedUser = null;
      try {
        storedUser = await fetchUsers(cleanEmail);
      } catch (fetchErr) {
        console.warn("fetchUsers error during sign-in:", fetchErr);
      }

      if (!storedUser) {
        // Auto-provision new account if logging in for first time
        storedUser = {
          email: cleanEmail,
          name: cleanEmail.split("@")[0],
          country: "us",
        };
        try {
          await saveUser(storedUser);
        } catch (saveErr) {
          console.warn("saveUser error during sign-in auto-provision:", saveErr);
        }
      }

      localStorage.setItem("rememberedEmail", storedUser.email);
      localStorage.setItem("loggedInUserEmail", storedUser.email);
      localStorage.setItem("biometricEnabled", "true");
      localStorage.setItem("hasLoggedInOnce", "true");

      onLogin({
        email: storedUser.email,
        name: storedUser.name || storedUser.email.split("@")[0],
        country: storedUser.country || "",
      });
    } catch (err) {
      console.error("Sign-in verification error:", err);
      setError("Failed to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      {/* Brand Hero Section */}
      <div className="login-hero-section">
        <div className="login-brand-badge">
          <img src={pocketappslogo} alt={config.apptitle || "Pocket Expenses"} className="login-brand-logo-img" />
        </div>
        <h1 className="login-hero-title">
          {config.apptitle}
          {process.env.REACT_APP_ENV === "QA" && (
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                background: "rgba(239, 135, 103, 0.2)",
                color: "var(--accent-coral)",
                padding: "2px 8px",
                borderRadius: "10px",
                marginLeft: "8px",
                verticalAlign: "middle",
              }}
            >
              .
            </span>
          )}
        </h1>
      </div>

      {/* Main Login Card */}
      <div className="login-body-section">
        <div className="login-card">
          <div className="login-card-header">
            <h2 className="login-card-title">Welcome Back</h2>
            <p className="login-card-desc">{isOtpSent ? "Enter the 6-digit code sent to your email" : "Sign in with your email or continue as guest"}</p>
          </div>

          {error && (
            <div className="login-error-banner">
              <i className="fi fi-rr-exclamation"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={isOtpSent ? handleVerifyLogin : handleSendLoginOtp}>
            {/* Email Field */}
            <div className="login-field-group">
              <label className="login-field-label">Email Address</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <i className="fi fi-rr-envelope"></i>
                </span>
                <input
                  type={isEmailMasked ? "text" : "email"}
                  placeholder="Enter your email"
                  value={isEmailMasked ? maskEmail(email) : email}
                  disabled={isOtpSent || loading || sendingOtp}
                  onFocus={(e) => {
                    if (isEmailMasked) {
                      e.target.select();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (isEmailMasked && (e.key === "Backspace" || e.key === "Delete")) {
                      e.preventDefault();
                      setEmail("");
                      setIsEmailMasked(false);
                    }
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isEmailMasked) {
                      setIsEmailMasked(false);
                      if (val.length < maskEmail(email).length) {
                        setEmail("");
                      } else {
                        const typed = val.replace(maskEmail(email), "").toLowerCase().trim();
                        setEmail(typed);
                      }
                    } else {
                      setEmail(val.toLowerCase().trim());
                    }
                  }}
                  required
                  className="login-input-field"
                  autoComplete="username email"
                />
                {email && !isOtpSent && (
                  <button
                    type="button"
                    onClick={() => {
                      setEmail("");
                      setIsEmailMasked(false);
                    }}
                    className="login-input-action"
                    aria-label="Clear email"
                  >
                    <FiX />
                  </button>
                )}
                {isOtpSent && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsOtpSent(false);
                      setOtp("");
                      setError("");
                      setIsEmailMasked(false);
                    }}
                    className="login-input-action"
                    title="Change email"
                    aria-label="Change email"
                  >
                    <i className="fi fi-rr-pencil" style={{ fontSize: "14px" }}></i>
                  </button>
                )}
              </div>
            </div>

            {/* OTP Verification Code Field */}
            {isOtpSent && (
              <div className="login-field-group">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="login-field-label mb-0">Verification Code</label>
                  <button type="button" onClick={handleSendLoginOtp} className="login-forgot-link" disabled={sendingOtp}>
                    {sendingOtp ? "Sending..." : "Resend Code"}
                  </button>
                </div>
                <div className="login-input-wrap">
                  <span className="login-input-icon">
                    <i className="fi fi-rr-shield-check"></i>
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => {
                      const numericOnly = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setOtp(numericOnly);
                      if (numericOnly.length === 6) {
                        handleVerifyLogin(null, numericOnly);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (!/[0-9]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key) && !e.metaKey && !e.ctrlKey) {
                        e.preventDefault();
                      }
                    }}
                    required
                    autoFocus
                    maxLength={6}
                    className="login-input-field"
                    style={{ letterSpacing: "4px", fontWeight: "600", fontSize: "16px" }}
                  />
                </div>
                <small style={{ fontSize: "11.5px", color: "var(--text-muted)", marginTop: "4px", display: "block" }}>
                  A verification code has been sent to <strong>{maskEmail(email) || email}</strong>
                </small>
              </div>
            )}

            {/* Primary Action Button */}
            {!isOtpSent ? (
              <button type="submit" className="btn-coral-modern" disabled={sendingOtp || loading || !email}>
                {sendingOtp ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Sending OTP Code...</span>
                  </>
                ) : (
                  <>
                    <span>Get Sign In OTP Code</span>
                    <i className="fi fi-rr-paper-plane"></i>
                  </>
                )}
              </button>
            ) : (
              <button type="submit" className="btn-coral-modern" disabled={loading || !otp}>
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <span>Verify & Sign In</span>
                    <i className="fi fi-rr-arrow-right"></i>
                  </>
                )}
              </button>
            )}

            {/* Biometric Face ID / Fingerprint Button (only for verified returning users who haven't disabled biometric) */}
            {canShowFaceId && (
              <button type="button" className="btn-biometric-modern mt-3" onClick={handleBiometricLogin} disabled={loading}>
                <div className="btn-biometric-gloss" aria-hidden="true" />
                <div className="btn-biometric-content">
                  <i className={biometryType === "TouchID" ? "fi fi-rr-fingerprint" : "fi fi-rr-face-viewfinder"} style={{ fontSize: "18px" }}></i>
                  <span className="btn-biometric-title">Sign In with {getBiometricLabel(biometryType)}</span>
                </div>
              </button>
            )}
          </form>

          <div className="login-divider">
            <span>or</span>
          </div>

          {/* Continue as Guest Button */}
          <button type="button" className="btn-guest-modern" onClick={() => onLogin({ email: "guest", name: "Guest User", country: "" })}>
            <i className="fi fi-rr-user"></i>
            <span>Continue as Guest</span>
          </button>

          {/* Guest Warning Callout */}
          <div className="guest-warning-note">
            <i className="fi fi-rr-info" style={{ fontSize: "14px", flexShrink: 0 }}></i>
            <span>{config.appdescription2}</span>
          </div>

          {/* App Description Callout */}
          {config.appdescription && (
            <div className="login-app-description">
              <p className="login-app-description-text">{config.appdescription}</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer Bar */}
      <footer className="login-footer-bar">
        <div>Copyright @ {new Date().getFullYear()} Unique Creations</div>
        <div className="login-footer-links">
          <Link to="/public/privacypolicy" className="login-footer-link">
            Privacy Policy
          </Link>
          <span style={{ color: "var(--border-subtle)" }}>•</span>
          <Link to="/public/contact" className="login-footer-link">
            Contact Us
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
